import { useState, useCallback, useRef, useEffect } from "react";
import supabaseClient from "../lib/supabaseClient";

// Types
export type GenerationType = "insight" | "email";

export type GenerationStatus =
  | "completed"
  | "sent"
  | "draft"
  | "failed"
  | "pending"
  | "in_progress";

export interface GenerationHistoryItem {
  id: string;
  type: GenerationType;
  title: string;
  preview: string;
  status: GenerationStatus;
  createdAt: string;
  updatedAt?: string;
  dataSources: Array<{
    id: string;
    type: string;
    name?: string;
  }>;
  metadata: {
    model?: string;
    processingTimeMs?: number;
    confidenceScore?: number;
    insightType?: string;
    insightCategory?: string;
    question?: string;
    wordCount?: number;
    recipientCount?: number;
    successCount?: number;
    failureCount?: number;
    openCount?: number;
    emailSubject?: string;
  };
  fullContent?: {
    headline?: string;
    narrative?: string;
    keyFindings?: string[];
    recommendations?: string[];
    emailBody?: string;
  };
}

export interface GenerationHistoryFilters {
  dateRange?: {
    start: string;
    end: string;
  };
  status?: GenerationStatus[];
  type?: GenerationType[];
  integrations?: string[];
  searchQuery?: string;
}

export interface GenerationHistoryResponse {
  items: GenerationHistoryItem[];
  total: number;
  hasMore: boolean;
  offset: number;
  limit: number;
}

export interface UseGenerationHistoryReturn {
  items: GenerationHistoryItem[];
  total: number;
  hasMore: boolean;
  isLoading: boolean;
  isLoadingMore: boolean;
  error: string | null;
  filters: GenerationHistoryFilters;
  setFilters: (filters: GenerationHistoryFilters) => void;
  refresh: () => Promise<void>;
  loadMore: () => Promise<void>;
  deleteItem: (id: string, type: GenerationType) => Promise<boolean>;
  getItem: (id: string) => Promise<GenerationHistoryItem | null>;
}

const API_BASE_URL =
  process.env.REACT_APP_API_URL || "https://api.sendupdates.ai";

// Helper function to get auth headers
const getAuthHeaders = async (): Promise<Record<string, string>> => {
  try {
    const { data } = await supabaseClient.auth.getSession();
    if (data.session?.access_token) {
      return {
        Authorization: `Bearer ${data.session.access_token}`,
      };
    }
  } catch (error) {
    console.error("Error getting auth headers:", error);
  }
  return {};
};

// Cache management
const cache = new Map<
  string,
  { data: GenerationHistoryResponse; timestamp: number }
>();
const CACHE_TTL = 2 * 60 * 1000; // 2 minutes

function getCacheKey(filters: GenerationHistoryFilters, offset: number): string {
  return `history_${JSON.stringify(filters)}_${offset}`;
}

export function useGenerationHistory(
  initialLimit = 20
): UseGenerationHistoryReturn {
  const [items, setItems] = useState<GenerationHistoryItem[]>([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFiltersState] = useState<GenerationHistoryFilters>({});

  const offsetRef = useRef(0);
  const limitRef = useRef(initialLimit);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  const fetchHistory = useCallback(
    async (
      currentFilters: GenerationHistoryFilters,
      offset = 0,
      append = false
    ) => {
      // Check cache first
      const cacheKey = getCacheKey(currentFilters, offset);
      const cached = cache.get(cacheKey);
      if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
        if (append) {
          setItems((prev) => [...prev, ...cached.data.items]);
        } else {
          setItems(cached.data.items);
        }
        setTotal(cached.data.total);
        setHasMore(cached.data.hasMore);
        return;
      }

      try {
        const authHeaders = await getAuthHeaders();

        // Build query params
        const params = new URLSearchParams();
        params.set("limit", limitRef.current.toString());
        params.set("offset", offset.toString());
        params.set("sortBy", "created_at");
        params.set("sortOrder", "desc");

        if (currentFilters.dateRange) {
          params.set("dateStart", currentFilters.dateRange.start);
          params.set("dateEnd", currentFilters.dateRange.end);
        }

        if (currentFilters.status && currentFilters.status.length > 0) {
          params.set("status", currentFilters.status.join(","));
        }

        if (currentFilters.type && currentFilters.type.length > 0) {
          params.set("type", currentFilters.type.join(","));
        }

        if (
          currentFilters.integrations &&
          currentFilters.integrations.length > 0
        ) {
          params.set("integrations", currentFilters.integrations.join(","));
        }

        if (currentFilters.searchQuery) {
          params.set("search", currentFilters.searchQuery);
        }

        const response = await fetch(
          `${API_BASE_URL}/insights/history?${params.toString()}`,
          {
            headers: {
              "Content-Type": "application/json",
              ...authHeaders,
            },
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const result = await response.json();

        if (!result.success) {
          throw new Error(result.error || "Failed to fetch history");
        }

        const data = result.data as GenerationHistoryResponse;

        // Update cache
        cache.set(cacheKey, { data, timestamp: Date.now() });

        if (append) {
          setItems((prev) => [...prev, ...data.items]);
        } else {
          setItems(data.items);
        }
        setTotal(data.total);
        setHasMore(data.hasMore);
        offsetRef.current = offset + data.items.length;
        setError(null);
      } catch (err) {
        console.error("Error fetching generation history:", err);
        setError(err instanceof Error ? err.message : "Failed to fetch history");
      }
    },
    []
  );

  // Initial load
  useEffect(() => {
    setIsLoading(true);
    fetchHistory(filters, 0, false).finally(() => setIsLoading(false));
  }, []);

  // Debounced filter change handler
  const setFilters = useCallback(
    (newFilters: GenerationHistoryFilters) => {
      setFiltersState(newFilters);

      // Debounce the fetch
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }

      debounceRef.current = setTimeout(() => {
        setIsLoading(true);
        offsetRef.current = 0;
        fetchHistory(newFilters, 0, false).finally(() => setIsLoading(false));
      }, 300);
    },
    [fetchHistory]
  );

  const refresh = useCallback(async () => {
    setIsLoading(true);
    offsetRef.current = 0;
    // Clear cache for current filters
    cache.clear();
    await fetchHistory(filters, 0, false);
    setIsLoading(false);
  }, [filters, fetchHistory]);

  const loadMore = useCallback(async () => {
    if (!hasMore || isLoadingMore) return;

    setIsLoadingMore(true);
    await fetchHistory(filters, offsetRef.current, true);
    setIsLoadingMore(false);
  }, [filters, hasMore, isLoadingMore, fetchHistory]);

  const deleteItem = useCallback(
    async (id: string, type: GenerationType): Promise<boolean> => {
      try {
        const authHeaders = await getAuthHeaders();

        const response = await fetch(
          `${API_BASE_URL}/insights/history/${id}?type=${type}`,
          {
            method: "DELETE",
            headers: {
              "Content-Type": "application/json",
              ...authHeaders,
            },
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const result = await response.json();

        if (result.success) {
          // Remove from local state
          setItems((prev) => prev.filter((item) => item.id !== id));
          setTotal((prev) => prev - 1);
          // Clear cache
          cache.clear();
          return true;
        }

        return false;
      } catch (err) {
        console.error("Error deleting history item:", err);
        return false;
      }
    },
    []
  );

  const getItem = useCallback(
    async (id: string): Promise<GenerationHistoryItem | null> => {
      try {
        const authHeaders = await getAuthHeaders();

        const response = await fetch(`${API_BASE_URL}/insights/history/${id}`, {
          headers: {
            "Content-Type": "application/json",
            ...authHeaders,
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const result = await response.json();

        if (result.success) {
          return result.data as GenerationHistoryItem;
        }

        return null;
      } catch (err) {
        console.error("Error fetching history item:", err);
        return null;
      }
    },
    []
  );

  return {
    items,
    total,
    hasMore,
    isLoading,
    isLoadingMore,
    error,
    filters,
    setFilters,
    refresh,
    loadMore,
    deleteItem,
    getItem,
  };
}
